import { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Chip,
  Collapse,
  Group,
  Loader,
  Paper,
  Progress,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconSearch } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useBadgeVariant } from "@hooks/useBadgeVariant";
import {
  getHeadsetGroups,
  installOnOnlineHeadsets,
} from "@utils/api-communication/HeadsetReleasesCli";
import type {
  HeadsetGroup,
  HeadsetGroupBy,
  HeadsetGroupsResult,
  HeadsetProgressFilter,
} from "types/headsetReleaseTypes";
import { ExpandToggle } from "./ExpandToggle";
import { HeadsetList } from "./HeadsetList";
import { notifyError } from "./notifyError";
import { percentOf } from "./releaseFormat";

const GROUP_BY: HeadsetGroupBy[] = ["distributor", "org", "version", "model"];
type StatusFilter = HeadsetProgressFilter | "all";

interface ReleaseHeadsetsProps {
  version: string;
  started: boolean;
  entryId?: string;
  defaultGroupBy?: HeadsetGroupBy;
}

export function ReleaseHeadsets({
  version,
  started,
  entryId,
  defaultGroupBy = "distributor",
}: ReleaseHeadsetsProps) {
  const { t } = useTranslation();
  const badgeVariant = useBadgeVariant();
  const [groupBy, setGroupBy] = useState<HeadsetGroupBy>(defaultGroupBy);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search.trim(), 300);
  const [expanded, setExpanded] = useState<string>();
  const [result, setResult] = useState<HeadsetGroupsResult>();
  const status = filter === "all" ? undefined : filter;

  useEffect(() => {
    let current = true;
    getHeadsetGroups(version, { groupBy, entryId, status })
      .then((groups) => current && setResult(groups))
      .catch(notifyError);
    return () => {
      current = false;
    };
  }, [version, groupBy, entryId, status]);

  const installGroup = async (group: HeadsetGroup) => {
    try {
      const { sent } = await installOnOnlineHeadsets(version, {
        entryId,
        groupBy,
        groupKey: group.key,
      });
      notifications.show({
        color: "green",
        message: t("headsetReleases.installSentToGroup", { count: sent }),
      });
    } catch (error) {
      notifyError(error);
    }
  };

  if (!result) return <Loader size="sm" />;
  const { counts } = result;
  const total = counts.done + counts.pending;
  const pendingLabel = started ? "pending" : "waiting";

  return (
    <Stack gap="sm">
      <Progress.Root size={14} aria-label={t("headsetReleases.statusBar")}>
        <Progress.Section
          value={total ? (counts.done / total) * 100 : 0}
          color="green"
          onClick={() => setFilter("done")}
          style={{ cursor: "pointer" }}
        />
        <Progress.Section
          value={total ? (counts.pending / total) * 100 : 0}
          color={started ? "blue" : "gray.4"}
          onClick={() => setFilter("pending")}
          style={{ cursor: "pointer" }}
        />
      </Progress.Root>

      <Group justify="space-between" gap="sm">
        <Chip.Group value={filter} onChange={(value) => setFilter(value as StatusFilter)}>
          <Group gap={6}>
            <Chip value="all" size="sm">
              {t("headsetReleases.filter.all", { count: total })}
            </Chip>
            <Chip value="pending" size="sm" color="blue">
              {t(`headsetReleases.filter.${pendingLabel}`, { count: counts.pending })}
            </Chip>
            <Chip value="done" size="sm" color="green">
              {t("headsetReleases.filter.done", { count: counts.done })}
            </Chip>
          </Group>
        </Chip.Group>
        <Group gap="sm">
          <SegmentedControl
            size="xs"
            aria-label={t("headsetReleases.groupBy")}
            value={groupBy}
            onChange={(value) => {
              setGroupBy(value as HeadsetGroupBy);
              setExpanded(undefined);
            }}
            data={GROUP_BY.map((value) => ({
              value,
              label: t(`headsetReleases.groupByOption.${value}`),
            }))}
          />
          <TextInput
            size="xs"
            w={220}
            leftSection={<IconSearch size={14} />}
            placeholder={t("headsetReleases.searchHeadsets")}
            aria-label={t("headsetReleases.searchHeadsets")}
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
        </Group>
      </Group>

      {debouncedSearch ? (
        <HeadsetList
          version={version}
          selection={{ entryId }}
          status={status}
          search={debouncedSearch}
        />
      ) : (
        <Paper withBorder radius="sm">
          {result.groups.map((group) => {
            const isOpen = expanded === group.key;
            const pending = group.total - group.done;
            return (
              <div
                key={group.key}
                style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}
              >
                <Group px="sm" py={6} gap="sm" wrap="nowrap">
                  <ExpandToggle
                    open={isOpen}
                    name={group.name || "-"}
                    onToggle={() => setExpanded(isOpen ? undefined : group.key)}
                  />
                  <Text fw={600} size="sm" style={{ flex: 1, minWidth: 0 }} truncate>
                    {group.name || "-"}
                  </Text>
                  <Progress value={percentOf(group)} color="green" size="sm" w={200} />
                  <Text size="sm" w={90} style={{ fontVariantNumeric: "tabular-nums" }}>
                    {t("headsetReleases.groupCount", { done: group.done, total: group.total })}
                  </Text>
                  <Badge
                    variant={badgeVariant}
                    color={started ? "blue" : "gray"}
                    w={110}
                    style={{ visibility: pending ? "visible" : "hidden" }}
                  >
                    {t(`headsetReleases.groupPending.${pendingLabel}`, { count: pending })}
                  </Badge>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    disabled={!started || !pending}
                    onClick={() => installGroup(group)}
                  >
                    {t("headsetReleases.installOnline")}
                  </Button>
                </Group>
                <Collapse in={isOpen}>
                  {isOpen && (
                    <div style={{ padding: "0 12px 12px 48px" }}>
                      <HeadsetList
                        version={version}
                        selection={{ entryId, groupBy, groupKey: group.key }}
                        status={status}
                      />
                    </div>
                  )}
                </Collapse>
              </div>
            );
          })}
          {!result.groups.length && (
            <Text size="sm" c="dimmed" p="sm">
              {t("headsetReleases.noHeadsets")}
            </Text>
          )}
          {result.remainingGroups > 0 && (
            <Text size="sm" c="dimmed" p="sm">
              {t("headsetReleases.moreGroups", { count: result.remainingGroups })}
            </Text>
          )}
        </Paper>
      )}
    </Stack>
  );
}
